import { useMemo, useCallback, memo, useState, useEffect } from "react";
import { Text } from "@aidron/aidron-ds";

import { i18n } from "../../../translate/i18n";

import {
  ButtonWithModal,
  ChartSeries,  
  ClientFlowSection,
  IconButtonWithModal,
  LoadingContent,
  ProductAccuracyChart,
  ProductHierarchyHeading,
  PredictionChart
} from "../sharedComponents.index";

import { CustomHeading, CustomText } from "../../StyledComponents/styleComponents.index";

import PropTypes from 'prop-types';

import { useForecastStore } from "../../../viewModel/stores/forecastStore";
import { getAverageProductDrivers } from "../../../model/services/ProductsService";

import { LOADING_CONTENT_TYPE } from "../LoadingContent/loading.content.constants";
import { createPercentFormatter, textFormatter } from "../../../utils/formatter/formatter";
import { LOCATION_VALUES_FOR_PREDICTION_CHART } from "../../components.constants";

const { t } = i18n;

const DriversModalContent = ({ ctx }) => {
  const [productDrivers, setProductDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [clientFlow, setClientFlow] = useState("");

  const { productId, products } = ctx || {};
 
  const orderedProductDrivers = productDrivers?.sort((a, b) => b.value - a.value) ?? [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const drivers = await getAverageProductDrivers(productId);
        const filteredDrivers = drivers
          ? drivers.drivers.filter(driver => driver.driver !== "Fluxo de Clientes (Índices do Varejo no Brasil)")
          : [];
        setProductDrivers(filteredDrivers.length ? filteredDrivers : []);
        setClientFlow(
          drivers?.drivers.find(driver => driver.driver === "Fluxo de Clientes (Índices do Varejo no Brasil)")?.value
        );
      } catch (error) {
        console.error('Erro no fetchData:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [productId]);

  if (isLoading) {
    return (
      <LoadingContent label={"Carregando informações do produto.."} type={LOADING_CONTENT_TYPE.VIEW_DATA} mode={LOADING_CONTENT_TYPE.VIEW_DATA}/>
    )
  }

  if (!isLoading && productDrivers.length === 0) {
    return <p>Não há informações sobre impacto de venda deste produto</p>
  }

  return (
    <>
      <div style={{ marginBlockStart: '-20px' }}>
        <ProductHierarchyHeading ctx={{ productId: productId, products: products }} />
      </div>

      <div style={{ display: 'flex', marginBlockStart: '1.5rem' }}>
        <CustomHeading size="sm">{t("productDrivers")}</CustomHeading>
        <IconButtonWithModal
          icon="help-circle"
          modalHeading={`${t("help")}: ${t("productDrivers")}`}
          renderModalContent={() => <Text>{t("productDriversExplanation")}</Text>}/>
      </div>

      <div style={{ inlineSize: '100%', overflow: 'scroll' }}>
        <div style={{ minInlineSize: '600px', marginBlockStart: '1.5rem' }}>
          {orderedProductDrivers.map(driver => (
            <ChartSeries series={driver} key={driver.driver} />
          ))}
        </div>
      </div>

      <ClientFlowSection ctx={{ clientFlow: clientFlow }} />
    </>
  );
}

const AccuracyChartButton = memo(function AccuracyChartButton({ ctx }) {
  const { productId, products } = ctx || {};

  const chartCtx = useMemo(() => ({ productId, products }), [productId, products]);

  const renderModalContent = useCallback(() => (
    <div className="mx-5">
      <ProductAccuracyChart ctx={chartCtx} />
    </div>
  ), [chartCtx]);

  const buttonCtx = useMemo(() => ({
    label: t("accuracyAnalysis"),
    modalHeading: t("accuracyAnalysis"),
    modalWidth: "700px",
    renderModalContent: renderModalContent
  }), [renderModalContent]);

  return <ButtonWithModal ctx={buttonCtx} />;
});

const ProductIndexes = memo(function ProductIndexes({ ctx }) {

  const { prediction = {} } = ctx || {};

  const {
    realizedVsDemandDeviation,
    lastMonthSaleVariation,
    saleAnomalyProbability,
    aiAccuracyOverClient,
  } = prediction;

  const indexes = useMemo(() => [
    {
      label: t("realizedDeviationVsDemand"),
      value: createPercentFormatter({ 
        minimumFractionDigits: 0, maximumFractionDigits: 0 
      }).format(realizedVsDemandDeviation / 100),
    },
    {
      label: t("lastMonthSaleVariation"),
      value: createPercentFormatter({
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(lastMonthSaleVariation / 100),
    },
    {
      label: t("saleAnomalyProbability"),
      value: createPercentFormatter({}).format(saleAnomalyProbability / 100),
    },
    {
      label: t("aiAccuracyOverClient"),
      value: aiAccuracyOverClient === 0 || (aiAccuracyOverClient >= -0.04 && aiAccuracyOverClient <= 0.04)
        ? "0%"
        : createPercentFormatter({
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(aiAccuracyOverClient / 100),
    },
  ], [realizedVsDemandDeviation, lastMonthSaleVariation, saleAnomalyProbability, aiAccuracyOverClient]);

  const renderHelpModal = useCallback(() => (
    <>
      {indexes.map((index) => (
        <div style={{ margin: '0 20px 20px 20px', textAlign: 'start' }} key={index.label}>
          <CustomText weight="bold">{index.label}:</CustomText>
          <CustomText margin="0 0 0 10px" textAlign="justify">
            {index.text}
          </CustomText>
        </div>
      ))}
    </>
  ), [indexes]);

  if (!prediction) {
    return (
      <LoadingContent label={t("loadingData")} type={LOADING_CONTENT_TYPE.VIEW_DATA} mode={LOADING_CONTENT_TYPE.VIEW_DATA}/>
    )
  }

  return (
    <section style={{ inlineSize: '100%', display: 'flex', justifyContent: 'center' }}>
      <div style={{ overflow: 'scroll' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CustomText fontSize={['0.3rem', '0.7rem', '1.2rem']} weight="bold" display="inline">
            {textFormatter(t("indexesToSupport"), { textTransform: 'uppercase' })}
          </CustomText>

          {/* <IconButtonWithModal
            icon="help-circle"
            modalHeading={`${t("help")}: ${textFormatter(t("indexesToSupport"), { textTransform: 'lowercase'})}`}
            renderModalContent={renderHelpModal}/> */}
        </div>

        <section style={{ overflow: 'scroll' }}>
          <div style={{ padding: '0 1.5rem', marginBlockStart: '1rem', display: 'flex', gap: '5px' }}>
            {indexes.map((index) => (
              <section
                key={index.label}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  borderRadius: '8px',
                  boxShadow: 'rgba(0, 0, 0, 0.08) 0px 5px 11px 0px',
                  minInlineSize: 'fit-content',
                  margin: '0 0.25rem 0.75rem 0.25rem',
                  padding: '0 1rem 0.5rem 1rem'
                }}>
                <div style={{ inlineSize: '110px', textAlign: 'center' }}>
                  <Text weight="bold" size="xs">{index.label}</Text>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <CustomHeading size="md" color="orange">{index.value}</CustomHeading>
                </div>
              </section>
            ))}
          </div>
        </section>
      </div>
    </section>
  );
});

export function ProductAnalysisChart({ ctx }) {

  const { productId, products } = ctx || {}
  const latestForecast = useForecastStore((s) => s.latestForecast);

  const { predictions = [], month, year } = latestForecast || {};

  const memoizedCtx = useMemo(() => ({ productId, products }), [products, productId]);

  const currentProductPrediction = predictions.find(
    (p) =>
      p.productId === productId &&
      p.month === month &&
      p.year === year
  );

  const indexesCtx = useMemo(
    () => ({ prediction: currentProductPrediction }),
    [currentProductPrediction]
  );

  const renderDriversModalContent = useCallback(() => (
    <DriversModalContent ctx={{ productId: productId, products: products }}/>
  ), [productId, products]);

  const driversButtonCtx = useMemo(() => ({
    label: t("salesImpact"),
    modalWidth: "700px",
    renderModalContent: renderDriversModalContent
  }), [renderDriversModalContent]);

  return (
    <>
      <section 
        style={{
          inlineSize: '70%',
          marginBlockStart: '-30px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
        <div style={{ inlineSize: '90%' }}>
          <ProductHierarchyHeading ctx={memoizedCtx} />

          <PredictionChart
            ctx={memoizedCtx}
            uiState={{ 
              clientPlanValuesFromPastMonths: true,
              location: LOCATION_VALUES_FOR_PREDICTION_CHART.OPPORTUNITY_ACCORDION_ADVANCED_OPP_BTN,
            }}/>
        </div>
      </section>

      <ProductIndexes ctx={indexesCtx} />

      <div 
        style={{ 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '15px',
          marginBlockEnd: '15px'
        }}>
        <ButtonWithModal ctx={driversButtonCtx} />
        <ProductAnalysisChart.BtnToHighlightClientAndIAAccuracy ctx={memoizedCtx}/>
      </div>      
    </>
  );
};

ProductAnalysisChart.BtnToHighlightClientAndIAAccuracy = AccuracyChartButton;

ProductAnalysisChart.propTypes = { ctx: PropTypes.shape() };
AccuracyChartButton.propTypes = { ctx: PropTypes.shape() };
DriversModalContent.propTypes = { productId: PropTypes.number };
ProductIndexes.propTypes = { ctx: PropTypes.shape() };